import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Menu, X, ChevronDown, Crown, LogOut, 
  Settings, CreditCard 
} from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_b7533cee-6cc1-4a41-9e88-4b20184fec89/artifacts/eixt3fs2_STM%20mobile%20logo.png';

// Brand name component
const BrandName = ({ className = "" }) => (
  <span 
    className={`font-semibold transition-all duration-200 ease-in-out cursor-default ${className}`}
    style={{ 
      fontFamily: "'Inter', sans-serif",
      letterSpacing: '-0.01em',
      background: 'linear-gradient(90deg, #00E5FF 0%, #3B82F6 50%, #8B5CF6 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.filter = 'brightness(1.12)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.filter = 'brightness(1)';
    }}
  >
    ScopeTheMarket
  </span>
);

const MarketingHeader = ({ 
  navLinks = [], 
  showFeaturesLink = false,
  height = "h-[100px]",
  logoSize = "h-[80px]",
  mobilLogoSize = "h-10"
}) => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isPremium, logout, loading } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  
  // Refs for dropdown
  const menuTriggerRef = useRef(null);
  const menuRef = useRef(null);

  // Get user initial for avatar
  const getUserInitial = () => {
    if (user?.name) return user.name[0].toUpperCase();
    if (user?.email) return user.email[0].toUpperCase();
    return 'U';
  };

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideMenu = menuRef.current?.contains(event.target);
      const clickedInsideTrigger = menuTriggerRef.current?.contains(event.target);
      
      if (!clickedInsideMenu && !clickedInsideTrigger) {
        setShowUserMenu(false);
      }
    };
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setShowUserMenu(false);
        setMobileMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    setMobileMenuOpen(false);
    navigate('/');
  };

  // Render authenticated user menu (avatar + dropdown)
  const renderAuthenticatedMenu = () => (
    <div className="relative">
      <button
        ref={menuTriggerRef}
        onClick={() => setShowUserMenu(!showUserMenu)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg transition-colors"
        style={{
          backgroundColor: showUserMenu ? 'rgba(255,255,255,0.05)' : 'transparent'
        }}
        data-testid="user-avatar-btn"
      >
        {/* Avatar Circle */}
        <div 
          className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium transition-all"
          style={{ 
            background: isPremium 
              ? 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)' 
              : 'linear-gradient(135deg, #3B82F6 0%, #06B6D4 100%)',
            color: '#FFFFFF',
            boxShadow: showUserMenu ? '0 0 0 2px rgba(59, 130, 246, 0.3)' : 'none'
          }}
        >
          {getUserInitial()}
        </div>
        {isPremium && (
          <Crown className="w-4 h-4" style={{ color: '#FBBF24' }} />
        )}
        <ChevronDown 
          className="w-4 h-4 transition-transform" 
          style={{ 
            color: '#64748B',
            transform: showUserMenu ? 'rotate(180deg)' : 'rotate(0deg)'
          }} 
        />
      </button>

      {/* Dropdown Menu - Positioned absolutely below avatar */}
      {showUserMenu && (
        <div 
          ref={menuRef}
          className="absolute right-0 mt-2 w-56 rounded-xl py-2"
          style={{
            top: '100%',
            background: '#0E1324',
            border: '1px solid rgba(59, 130, 246, 0.15)',
            boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
            zIndex: 9999
          }}
          data-testid="user-dropdown-menu"
        >
          {/* User Info Header */}
          <div className="px-4 py-3 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
            <p className="text-sm font-medium truncate" style={{ color: '#F8FAFC' }}>
              {user?.email}
            </p>
            <p className="text-xs mt-0.5" style={{ color: isPremium ? '#FBBF24' : '#64748B' }}>
              {isPremium ? 'Pro Plan' : 'Free Plan'}
            </p>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            {/* Account Settings */}
            <Link
              to="/app/config"
              onClick={() => setShowUserMenu(false)}
              className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors"
              style={{ color: '#94A3B8' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              data-testid="dropdown-account-settings"
            >
              <Settings className="w-4 h-4" />
              Account Settings
            </Link>

            {/* Manage Plan */}
            <Link
              to="/app/billing"
              onClick={() => setShowUserMenu(false)}
              className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors"
              style={{ color: '#94A3B8' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              data-testid="dropdown-manage-plan"
            >
              <CreditCard className="w-4 h-4" />
              Manage Plan
            </Link>

            <div className="my-1 h-px" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }} />

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors w-full text-left"
              style={{ color: '#f87171' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(248,113,113,0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              data-testid="dropdown-logout"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // Render unauthenticated buttons (Login + Get Started)
  const renderUnauthenticatedButtons = () => (
    <div className="flex items-center gap-3">
      <Link
        to="/login"
        className="px-4 py-2 text-sm font-medium transition-all duration-200 rounded-lg"
        style={{ color: '#94A3B8' }}
        onMouseEnter={(e) => e.currentTarget.style.color = '#E2E8F0'}
        onMouseLeave={(e) => e.currentTarget.style.color = '#94A3B8'}
        data-testid="header-login-btn"
      >
        Login
      </Link>
      <Link
        to="/signup"
        className="px-5 py-2 text-sm font-medium rounded-lg transition-all duration-200"
        style={{ background: '#3B82F6', color: 'white' }}
        onMouseEnter={(e) => e.currentTarget.style.filter = 'brightness(1.1)'}
        onMouseLeave={(e) => e.currentTarget.style.filter = 'brightness(1)'}
        data-testid="header-signup-btn"
      >
        Get Started
      </Link>
    </div>
  );

  // Loading skeleton for auth state
  const renderAuthSkeleton = () => (
    <div className="flex items-center gap-3">
      <div 
        className="w-9 h-9 rounded-full animate-pulse"
        style={{ backgroundColor: 'rgba(148, 163, 184, 0.2)' }}
      />
    </div>
  );

  return (
    <header className={`relative z-50 ${height}`} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-full relative">
        {/* Desktop Layout */}
        <div className="hidden md:flex items-center justify-between h-full">
          {/* LEFT: Brand Lockup */}
          <Link to="/" className="flex items-center -space-x-3" data-testid="header-logo">
            <img 
              src={LOGO_URL} 
              alt="ScopeTheMarket" 
              className={`${logoSize} w-auto`}
            />
            <BrandName className="text-lg" />
          </Link>

          {/* CENTER: Navigation Links */}
          <nav className="absolute left-1/2 -translate-x-1/2 flex items-center gap-8">
            {showFeaturesLink && (
              <Link 
                to="#features"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="text-sm font-medium transition-all duration-200"
                style={{ color: '#94A3B8' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#E2E8F0'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#94A3B8'}
              >
                Features
              </Link>
            )}
            {navLinks.map((link) => (
              <Link 
                key={link.to}
                to={link.to}
                className="text-sm font-medium transition-all duration-200"
                style={{ color: '#94A3B8' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#E2E8F0'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#94A3B8'}
              >
                {link.label}
              </Link>
            ))}
            {/* Dashboard link for authenticated users */}
            {isAuthenticated && (
              <Link 
                to="/app"
                className="text-sm font-medium transition-all duration-200"
                style={{ color: '#94A3B8' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#E2E8F0'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#94A3B8'}
                data-testid="header-dashboard-link"
              >
                Dashboard
              </Link>
            )}
          </nav>

          {/* RIGHT: Auth Section */}
          {loading ? renderAuthSkeleton() : (
            isAuthenticated ? renderAuthenticatedMenu() : renderUnauthenticatedButtons()
          )}
        </div>

        {/* Mobile Layout */}
        <div className="flex md:hidden items-center justify-between h-full">
          {/* Brand Lockup */}
          <Link to="/" className="flex items-center gap-1.5" data-testid="header-logo-mobile">
            <img 
              src={LOGO_URL} 
              alt="ScopeTheMarket" 
              className={`${mobilLogoSize} w-auto`}
            />
            <BrandName className="text-sm" />
          </Link>
          
          {/* Right side: Avatar or Menu */}
          <div className="flex items-center gap-2">
            {/* Show avatar on mobile if authenticated */}
            {!loading && isAuthenticated && (
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                ref={menuTriggerRef}
                className="p-1"
                data-testid="mobile-user-avatar"
              >
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium"
                  style={{ 
                    background: isPremium 
                      ? 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)' 
                      : 'linear-gradient(135deg, #3B82F6 0%, #06B6D4 100%)',
                    color: '#FFFFFF'
                  }}
                >
                  {getUserInitial()}
                </div>
              </button>
            )}
            
            {/* Hamburger Menu */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 -mr-2 rounded-lg transition-colors"
              style={{ color: '#94A3B8' }}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden absolute top-full left-0 right-0 px-6 pb-6 pt-4"
          style={{ 
            backgroundColor: '#0B0F1A',
            borderBottom: '1px solid rgba(255,255,255,0.06)' 
          }}
          data-testid="mobile-menu-panel"
        >
          <nav className="flex flex-col gap-1">
            {showFeaturesLink && (
              <Link 
                to="#features"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
                  setMobileMenuOpen(false);
                }}
                className="text-sm font-medium py-3 px-2 rounded-lg transition-colors"
                style={{ color: '#94A3B8' }}
              >
                Features
              </Link>
            )}
            {navLinks.map((link) => (
              <Link 
                key={link.to}
                to={link.to}
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-medium py-3 px-2 rounded-lg transition-colors"
                style={{ color: '#94A3B8' }}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Auth-specific mobile items */}
            {!loading && isAuthenticated ? (
              <>
                <Link 
                  to="/app"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-sm font-medium py-3 px-2 rounded-lg transition-colors"
                  style={{ color: '#94A3B8' }}
                >
                  Dashboard
                </Link>
                <Link 
                  to="/app/config"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-sm font-medium py-3 px-2 rounded-lg transition-colors"
                  style={{ color: '#94A3B8' }}
                >
                  Account Settings
                </Link>
                <Link 
                  to="/app/billing"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-sm font-medium py-3 px-2 rounded-lg transition-colors"
                  style={{ color: '#94A3B8' }}
                >
                  Manage Plan
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-sm font-medium py-3 px-2 rounded-lg transition-colors text-left"
                  style={{ color: '#f87171' }}
                >
                  Logout
                </button>
              </>
            ) : !loading && (
              <>
                <Link 
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-sm font-medium py-3 px-2 rounded-lg transition-colors"
                  style={{ color: '#94A3B8' }}
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  onClick={() => setMobileMenuOpen(false)}
                  className="mt-3 px-5 py-3 text-sm font-medium rounded-lg text-center transition-all duration-200"
                  style={{ background: '#3B82F6', color: 'white' }}
                >
                  Get Started
                </Link>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default MarketingHeader;
